import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { Customer } from '@df/session/api/customer';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentField } from '@icms/core/content';
import { LocalCustomerModel } from '../../../customer/customer.model.decorator';
import { UserImageRole } from '../../../customer/user-images.service';
import { NAntavo } from '../../../module/antavo/antavo.interface';
import { AntavoService } from '../../../module/antavo/antavo.service';
import { IAccountProfilesResolverData } from './account-profiles.resolver';
import { NProfiles } from './profile.interfaces';

// Use component ID from the consolidated interfaces
const componentId = NProfiles.COMPONENT_IDS.accountProfiles;

@Component({
    selector: 'account-profiles',
    templateUrl: './account-profiles.html',
    styleUrls: ['./account-profiles.scss'],
    standalone: true,
    imports: [TranslationModule, RouterLink, ICmsClientModule, CommonModule, DfImageDirective],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<AccountProfilesComponent>({
    id: componentId,
    name: 'Account Profiles',
    block: {
        url: componentId
    },
    fields: [
        {
            key: 'antavoMissionId',
            type: NICmsContentField.EFieldType.text,
            title: 'Antavo Mission ID',
            description: 'The ID of the Antavo mission to check for profile completion'
        }
    ]
})
export class AccountProfilesComponent extends UiComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NProfiles.IAccountProfilesContent;
    private customer = inject(Customer);
    private antavoService = inject(AntavoService);
    private route = inject(ActivatedRoute);
    protected customerModel = inject(LocalCustomerModel);
    protected profileData: NProfiles.IProfileData = {};
    protected mission?: NAntavo.IChallenge;
    protected showBanner = false;

    override async ngOnInit() {
        // Try to get resolver data first (when used as a standalone route)
        const resolverData = this.route.snapshot.data['profilesData'] as IAccountProfilesResolverData;
        if (resolverData) {
            // Use resolver data
            this.profileData = resolverData.profileData;
            this.mission = resolverData.mission;
            this.showBanner = resolverData.showBanner;
        } else {
            // Fallback to loading data directly (when used as embedded component)
            this.updateProfileData();
            await this.loadMission();
        }

        super.ngOnInit();
    }

    private updateProfileData(): void {
        const customerModel = this.customer.model as LocalCustomerModel;
        const profileJson = customerModel.data.customer_profile;

        if (profileJson) {
            try {
                this.profileData = JSON.parse(profileJson);
                this.detectChanges();
            } catch (error) {
                console.error('Failed to parse customer profile data:', error);
                this.profileData = {};
            }
        }
    }

    get customerProfile(): NProfiles.IMemberProfile | undefined {
        return this.profileData.customer;
    }

    get horses(): NProfiles.IHorseProfile[] {
        return this.profileData.horses || [];
    }

    get familyMembers(): NProfiles.IMemberProfile[] {
        return this.profileData.familyMembers || [];
    }

    get areProfilesEmpty(): boolean {
        return !this.customerProfile && !this.horses.length && !this.familyMembers.length;
    }

    private async loadMission(): Promise<void> {
        if (this.areProfilesEmpty) {
            this.mission = await this.antavoService.getProfileMission();
            this.showBanner = !!this.mission && this.mission.completions !== 1;
            this.detectChanges();
        }
    }

    get customerNickname(): string | undefined {
        return this.customerModel.data?.nickname;
    }

    /**
     * Get user image URL for the specified role and index
     */
    protected getUserImageUrl(imageRole: string, imageIndex?: number): string | undefined {
        if (!imageRole) {
            return undefined;
        }

        // Convert string to UserImageRole enum
        const role = imageRole as UserImageRole;
        return this.customerModel.getUserImage(role, imageIndex);
    }
}
